/**
 * The Orders sheet scan (PROD-1511): the second paper a school photographs.
 *
 * The chef's Orders sheet travels with the food and is the delivery record:
 * school staff write the Drop-off °F, tick Received and sign at drop-off
 * (PROD-1453). The `delivery` scan task reads that paper (server
 * `sheet-vision.ts`); this module is its two pure ends on the client, the
 * request rows and the write-back. The production record's own scan stays in
 * `spreadsheet-model.ts` (`buildRecordScanRows` / `applyRecordScan`).
 *
 * ONLY ROWS THAT STAND ON A TRUCK go on the wire: the sheet lists what was
 * packed, nothing else. Each row's `planned` is what was PACKED for its band
 * (the row's Prepared), which is what the sheet's Qty column prints.
 *
 * A TICK IS NOT A COUNT. It says the line arrived as packed, so a ticked row
 * takes the Received the screen already shows, as an explicit entry the
 * receipt names; a clearly EMPTY box rings Received amber and says so. A number
 * is never invented from a tick.
 *
 * Pure — no React, no fetch.
 */

import type { ScanSheetResponse } from '@/api/sheet-scan'
import { formatServiceDateLabel } from '@/lib/cafeteria/format'
import {
  cellOffered,
  type ReceivedScanPatch,
  type RecordColumn,
  type RecordRow,
  recordScanRowId,
  type RecordScanTarget,
  type RecordStep,
  tempCellId,
  type TempScanPatch,
  tempScopeKeyFor,
  tempStoreKeyFor,
} from '@/lib/production-records/spreadsheet-model'
import { buildPhotoWarnings, type ScanCellFlag, type ScanPartialSummary } from '@/lib/sheet-scan/apply-result'
import type { ScanRowWire } from '@/lib/sheet-scan/build-request'

/** The delivery task's two wire keys (server `DELIVERY_COLUMNS`). */
export const DELIVERY_TEMP_KEY = 'dropOffTemperatureF'
export const DELIVERY_CHECK_KEY = 'receivedChecked'

function lowerTemperatureClass(value: RecordRow['temperatureClass']): ScanRowWire['temperatureClass'] {
  switch (value) {
    case 'HOT':
      return 'hot'
    case 'CHILLED':
      return 'chilled'
    case 'AMBIENT':
      return 'ambient'
    default:
      return null
  }
}

/** What was packed for the row's band: its Prepared, else every truck line it stands on. */
function packedQuantity(row: RecordRow): number | null {
  const prepared = row.prefill.prepared.value
  if (prepared !== null) {
    return prepared
  }
  return row.trucks.length > 0 ? row.trucks.reduce((sum, truck) => sum + truck.plannedQuantity, 0) : null
}

/**
 * The `rows` half of a `task: 'delivery'` request for ONE step, plus the index
 * that routes the answer back. The row ids are the record scan's
 * (`recordScanRowId`), unique across the day, so several steps can share one
 * request and every answer still names its step.
 */
export function buildDeliveryScanRows(step: RecordStep): {
  rows: ScanRowWire[]
  index: Map<string, RecordScanTarget>
} {
  const rows: ScanRowWire[] = []
  const index = new Map<string, RecordScanTarget>()
  for (const row of step.rows) {
    if (row.trucks.length === 0) {
      continue
    }
    const rowId = recordScanRowId(step.key, row)
    rows.push({
      rowId,
      name: row.label,
      groupLabel: row.band,
      mealtimeLabel: step.title,
      temperatureClass: lowerTemperatureClass(row.temperatureClass),
      planned: packedQuantity(row),
      parentRowId: null,
    })
    index.set(rowId, { stepKey: step.key, row })
  }
  return { rows, index }
}

/**
 * The dates the Orders sheets can carry, for the reader's date check: every
 * delivery behind these steps, oldest first ("Thursday, October 8 or Friday,
 * October 9"). A sheet is printed for the day the food travels, which can be
 * the day before service. Falls back to the service date when no truck is known.
 */
export function deliveryDatesLabel(steps: readonly RecordStep[], serviceDate: string): string {
  const dates = [...new Set(steps.flatMap((step) => step.trucks.map((truck) => truck.deliveryDate.slice(0, 10))))]
    .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
    .sort()
  const labels = (dates.length > 0 ? dates : [serviceDate]).map((date) => formatServiceDateLabel(date))
  if (labels.length === 1) {
    return labels[0]!
  }
  return `${labels.slice(0, -1).join(', ')} or ${labels.at(-1)!}`
}

export interface DeliveryScanApplication {
  /** Ticked rows: the Received the screen shows, now an explicit entry. */
  received: ReceivedScanPatch[]
  /** Drop-off readings, one per item scope (the record's Received °F). */
  temps: TempScanPatch[]
  /** Low-confidence cells and clearly empty Received boxes. */
  flags: ScanCellFlag[]
  summary: ScanPartialSummary
}

/** A row's name with its band, as the warnings print it. */
function rowName(row: RecordRow): string {
  return row.band ? `${row.label} ${row.band}` : row.label
}

/** The Received cell's flag key: the SKU scope's on a weekly milk row (`countCellIdFor`'s format). */
function receivedFlagKey(stepKey: string, row: RecordRow): string {
  return row.milk ? row.milk.scopeKey : `${stepKey}::${row.key}`
}

/**
 * Route a `task: 'delivery'` response onto the rows.
 *
 * PATCHES, like `applyRecordScan`: the flow merges them over the LATEST state.
 * A tick confirms what the screen shows; an empty box never changes a number,
 * it flags the cell and adds one warning per row; a cell nobody could read is
 * simply absent. Drop-off readings follow the record scan's scope rule: the
 * first reading per item wins and a different second one flags the scope.
 */
export function applyDeliveryScan(
  result: ScanSheetResponse,
  index: ReadonlyMap<string, RecordScanTarget>,
  current: (row: RecordRow, column: RecordColumn) => number | null,
): DeliveryScanApplication {
  const received: ReceivedScanPatch[] = []
  const temps: TempScanPatch[] = []
  const flags: ScanCellFlag[] = []
  const warnings: string[] = []
  const tempsByScope = new Map<string, TempScanPatch>()
  const disagreed = new Set<string>()
  const unticked = new Set<string>()
  let filledCount = 0
  let lowConfidenceCount = 0

  for (const scanned of result.rows) {
    const target = index.get(scanned.rowId)
    if (!target) {
      continue
    }
    const { stepKey, row } = target

    const check = scanned.cells[DELIVERY_CHECK_KEY]
    if (check && check.value === 1) {
      const shown = current(row, 'received')
      if (shown !== null) {
        received.push({ stepKey, row, value: shown })
        filledCount += 1
        if (check.confidence === 'low') {
          lowConfidenceCount += 1
          flags.push({ rowKey: receivedFlagKey(stepKey, row), field: 'received' })
        }
      }
    } else if (check && check.value === 0) {
      flags.push({ rowKey: receivedFlagKey(stepKey, row), field: 'received' })
      const name = rowName(row)
      if (!unticked.has(name)) {
        unticked.add(name)
        warnings.push(`Received is not ticked for ${name} on the delivery sheet. Check what arrived.`)
      }
    }

    const dropOff = scanned.cells[DELIVERY_TEMP_KEY]
    if (dropOff && dropOff.value !== null && cellOffered(row, 'receivedTemp')) {
      const scopeKey = tempScopeKeyFor(row, 'receivedTemp')
      const scopeId = tempCellId(scopeKey, 'receivedTemp')
      const existing = tempsByScope.get(scopeId)
      if (!existing) {
        const patch: TempScanPatch = {
          stepKey,
          scopeKey,
          storeKey: tempStoreKeyFor(row, 'receivedTemp'),
          column: 'receivedTemp',
          value: dropOff.value,
          confidence: dropOff.confidence,
          row,
        }
        tempsByScope.set(scopeId, patch)
        temps.push(patch)
        filledCount += 1
        if (dropOff.confidence === 'low') {
          lowConfidenceCount += 1
          flags.push({ rowKey: scopeKey, field: 'receivedTemp' })
        }
      } else if (existing.value !== dropOff.value && !disagreed.has(scopeId)) {
        disagreed.add(scopeId)
        flags.push({ rowKey: scopeKey, field: 'receivedTemp' })
        warnings.push(`Two lines of ${row.label} show different drop-off temperatures on the sheet. Check the value.`)
      }
    }
  }

  return {
    received,
    temps,
    flags,
    summary: {
      filledCount,
      lowConfidenceCount,
      warnings: [...buildPhotoWarnings(result.images), ...warnings],
      unmatchedRowsSeen: result.unmatchedRowsSeen,
    },
  }
}

/**
 * Cells the Orders sheet can fill on these rows, the denominator of "Filled X
 * of Y": Received on every row, and the drop-off reading once per item scope.
 */
export function deliveryScanFieldTotal(index: ReadonlyMap<string, RecordScanTarget>): number {
  let total = 0
  const scopes = new Set<string>()
  for (const { row } of index.values()) {
    total += 1
    const scopeId = tempCellId(tempScopeKeyFor(row, 'receivedTemp'), 'receivedTemp')
    if (cellOffered(row, 'receivedTemp') && !scopes.has(scopeId)) {
      scopes.add(scopeId)
      total += 1
    }
  }
  return total
}

/**
 * The sign-off's "Received by", cleaned for the Delivery card: trimmed, inner
 * runs of space collapsed, capped like the card's own input (80). Null for an
 * empty or absent answer.
 */
export function receivedByFromScan(text: string | null | undefined): string | null {
  const cleaned = (text ?? '').replace(/\s+/g, ' ').trim().slice(0, 80)
  return cleaned === '' ? null : cleaned
}
